// Authored (non-voxel) unit models: hero and champion `.glb` files that replace
// a prefab's smooth voxel mesh while High Fidelity Units is on.
//
// A model is flattened into one BufferGeometry carrying the attribute set the
// batched voxel renderer expects (`color`, `aRoughness`, `aMetalness`,
// `aEmissive`, `aTeam`), so it rides the same InstancedMesh, material, team
// colouring and selection as every other unit — no second render path. Which
// part is the owner's colour comes from the material name: `team*`. A material
// with an emissive colour glows.
//
// Models load once, on demand. Until a model arrives, the prefab keeps its
// smooth voxel look; `useUnitModelsVersion` re-renders the batches when one does.
import { useEffect, useSyncExternalStore } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/** Prefab id → model URL (served from `public/`). Scripts: `scripts/blender/`. */
export const UNIT_MODEL_IDS = [
  'archmage',
  'bandit',
  'banshee',
  'blood_priest',
  'bowman',
  'bronze_smith',
  'catapult',
  'chaos_lord',
  'cleric',
  'dark_templar',
  'death_knight',
  'demon',
  'engineer',
  'explorer',
  'galleon',
  'goblin',
  'guard',
  'knight',
  'legend_dragon',
  'legend_golem',
  'legend_lich',
  'luminarch',
  'mage',
  'merchant_ship',
  'ogre',
  'orc',
  'orc_wolf_rider',
  'paladin',
  'seraphim',
  'settler',
  'skeleton',
  'skeleton_archer',
  'soldier',
  'sorcerer',
  'spy',
  'templar',
  'trade_ship',
  'transport_ship',
  'treant',
  'wagon',
  'warlord',
  'warship',
  'wizard',
  'wraith',
  'zealot',
  'zombie',
] as const;

/** Prefabs that wear another unit's model: the wild dragon is the champion dragon, unowned. */
const SHARED: Readonly<Record<string, string>> = { dragon: 'legend_dragon' };

export const UNIT_MODEL_URLS: Readonly<Record<string, string>> = Object.fromEntries([
  ...UNIT_MODEL_IDS.map((id) => [id, `/models/units/${id}.glb`] as const),
  ...Object.entries(SHARED).map(([id, as]) => [id, `/models/units/${as}.glb`] as const),
]);

/** Glow strength above this adds nothing the voxel shader can show. */
const MAX_GLOW = 1.5;

const models = new Map<string, THREE.BufferGeometry>();
const listeners = new Set<() => void>();
let version = 0;
let loading: Promise<void> | null = null;

const _c = new THREE.Color();

// Material classes: the tile order of the unit material atlas (`scripts/generate-unit-atlas.mjs`),
// the same numbers `packages/ui/src/prefabs/materials.ts` uses for voxel prefabs.
const MATERIAL_NONE = 0;
const MATERIAL_CLOTH = 1;
const MATERIAL_LEATHER = 2;
const MATERIAL_METAL = 3;
const MATERIAL_STONE = 4;
const MATERIAL_BONE = 5;
const MATERIAL_SKIN = 6;
const MATERIAL_HIDE = 7;
const MATERIAL_WOOD = 8;

/**
 * The texture class of a model material, read from its name (the Blender scripts name materials
 * for what they are) with glow overriding everything: a glowing surface has no grain.
 */
export function modelMaterialClass(name: string, glows = false): number {
  const n = name.toLowerCase();
  if (glows) return MATERIAL_NONE;
  if (/steel|plate|iron|gold|crown|buckle|armor|blade|metal|fuller/.test(n)) return MATERIAL_METAL;
  if (/stone|rock|moss/.test(n)) return n.includes('moss') ? MATERIAL_HIDE : MATERIAL_STONE;
  if (/bone|rib|tusk|claw|hornbone|horn|sclera|socket_bone/.test(n)) return MATERIAL_BONE;
  if (/skin|face|hand/.test(n)) return MATERIAL_SKIN;
  if (/hide|scale|leaf|leaves|feather|fur|mane|tail|hair/.test(n)) return MATERIAL_HIDE;
  if (/wood|staff|timber|mast|hull|deck|bark|haft|shaft|mast/.test(n)) return MATERIAL_WOOD;
  if (/leather|boot|glove|belt|pouch|quiver|pack|bedroll|scabbard|bandolier|rope|hoof/.test(n))
    return MATERIAL_LEATHER;
  return MATERIAL_CLOTH;
}

/**
 * Flatten a loaded model (every mesh, world transforms applied) into one
 * geometry with per-vertex material attributes. Null when it has no mesh.
 */
export function geometryFromScene(scene: THREE.Object3D): THREE.BufferGeometry | null {
  scene.updateMatrixWorld(true);
  const meshes: THREE.Mesh[] = [];
  scene.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh);
  });
  const parts: THREE.BufferGeometry[] = [];
  let anyTeam = false;
  for (const mesh of meshes) {
    const material = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as
      | THREE.MeshStandardMaterial
      | undefined;
    let geo = mesh.geometry.clone();
    geo.applyMatrix4(mesh.matrixWorld);
    if (geo.index) geo = geo.toNonIndexed();
    if (!geo.getAttribute('normal')) geo.computeVertexNormals();
    const n = geo.getAttribute('position').count;
    const isTeam = /^team/i.test(material?.name ?? '');
    anyTeam ||= isTeam;
    const color = material?.color ?? _c.set('#888888');
    const glow = material?.emissive
      ? material.emissive
          .clone()
          .multiplyScalar(Math.min(material.emissiveIntensity ?? 1, MAX_GLOW))
      : new THREE.Color(0, 0, 0);
    const colors = new Float32Array(n * 3);
    const rough = new Float32Array(n).fill(material?.roughness ?? 0.5);
    const metal = new Float32Array(n).fill(material?.metalness ?? 0.1);
    const emissive = new Float32Array(n * 3);
    const team = new Float32Array(n).fill(isTeam ? 1 : 0);
    const glows = !!material && material.emissiveIntensity > 0 && material.emissive.getHex() !== 0;
    const cls = new Float32Array(n).fill(modelMaterialClass(material?.name ?? '', glows));
    for (let i = 0; i < n; i++) {
      colors.set([color.r, color.g, color.b], i * 3);
      emissive.set([glow.r, glow.g, glow.b], i * 3);
    }
    const out = new THREE.BufferGeometry();
    out.setAttribute('position', geo.getAttribute('position'));
    out.setAttribute('normal', geo.getAttribute('normal'));
    out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    out.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    out.setAttribute('aRoughness', new THREE.BufferAttribute(rough, 1));
    out.setAttribute('aMetalness', new THREE.BufferAttribute(metal, 1));
    out.setAttribute('aEmissive', new THREE.BufferAttribute(emissive, 3));
    out.setAttribute('aTeam', new THREE.BufferAttribute(team, 1));
    out.setAttribute('aMat', new THREE.BufferAttribute(cls, 1));
    parts.push(out);
  }
  if (parts.length === 0) return null;
  const merged = mergeParts(parts);
  // No team material at all: fall back to the whole-body faction tint, as voxel prefabs do.
  if (!anyTeam) (merged.getAttribute('aTeam').array as Float32Array).fill(-1);
  return merged;
}

function mergeParts(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const first = parts[0] as THREE.BufferGeometry;
  if (parts.length === 1) return first;
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(first.attributes)) {
    const a = first.getAttribute(name) as THREE.BufferAttribute;
    const total = parts.reduce((s, p) => s + p.getAttribute(name).count, 0);
    const arr = new Float32Array(total * a.itemSize);
    let at = 0;
    for (const p of parts) {
      const src = p.getAttribute(name).array as Float32Array;
      arr.set(src, at);
      at += src.length;
    }
    out.setAttribute(name, new THREE.BufferAttribute(arr, a.itemSize));
  }
  return out;
}

/** The authored model for a prefab, once loaded. */
export function getUnitModelGeometry(prefabId: string): THREE.BufferGeometry | null {
  return models.get(prefabId) ?? null;
}

/** Load every registered model, once. Failures leave that prefab on its voxel mesh. */
export function loadUnitModels(): Promise<void> {
  if (loading) return loading;
  const loader = new GLTFLoader();
  loading = Promise.all(
    Object.entries(UNIT_MODEL_URLS).map(async ([id, url]) => {
      try {
        const gltf = await loader.loadAsync(url);
        const geo = geometryFromScene(gltf.scene);
        if (geo) models.set(id, geo);
      } catch (err) {
        console.warn(`unit model ${id} failed to load; keeping the voxel look`, err);
      }
    }),
  ).then(() => {
    version++;
    for (const l of listeners) l();
  });
  return loading;
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Re-render when the models arrive; starts loading the first time `enabled` is true. */
export function useUnitModelsVersion(enabled: boolean): number {
  useEffect(() => {
    if (enabled) void loadUnitModels();
  }, [enabled]);
  return useSyncExternalStore(subscribe, () => version);
}
